import { forwardRef, useId, type HTMLAttributes, type MouseEvent, type ReactElement, type ReactNode } from 'react'
import { Link, type To } from 'react-router-dom'
import { cn } from '@/lib/utils'
import './card.css'

export type CardElement = 'div' | 'section' | 'article' | 'li'
export type CardHeadingLevel = 2 | 3 | 4 | 5 | 6
/** neutral for an ordinary choice, such as a plan or a layout; media for a camera or video source (pink). */
export type CardSelectionTone = 'neutral' | 'media'

type CardActionBase = {
  /** Required. The action's accessible name. Start with the card’s title so voice control matches what people see: “Summit finish”, “Open Ridge to Coast”. */
  label: string
  onClick?: (event: MouseEvent<HTMLElement>) => void
}

/** The whole card as one action: a button, an app route (`to`) or any other address (`href`). */
export type CardAction =
  | (CardActionBase & { to?: undefined; href?: undefined })
  | (CardActionBase & { to: To; replace?: boolean; href?: undefined })
  | (CardActionBase & { href: string; to?: undefined })

export type CardProps = Omit<HTMLAttributes<HTMLElement>, 'title'> & {
  /** The card’s heading. */
  title?: ReactNode
  /** One short line under the title. */
  description?: ReactNode
  /** A Lucide icon before the title. */
  icon?: ReactElement
  /** Actions or a status at the end of the header, for example an IconButton or a Button. */
  actions?: ReactNode
  /** The element to render. A titled `section` or `article` is named by its title. */
  as?: CardElement
  /** The heading level for the title, so the page outline stays meaningful. */
  headingLevel?: CardHeadingLevel
  /** Shows the card as chosen. With a button `action`, the action becomes a toggle and reports it with `aria-pressed`. */
  selected?: boolean
  /** How a selected card looks: neutral (default) or media, the pink selection for cameras and video sources. */
  selectionTone?: CardSelectionTone
  /**
   * Makes the whole card one action without nesting controls: a real button or link stretched over the card.
   * Buttons, links and fields inside the card stay above it and keep working.
   */
  action?: CardAction
  children?: ReactNode
}

function CardActionControl({ action, pressed, describedBy }: { action: CardAction; pressed?: boolean; describedBy?: string }) {
  const content = <span className="sr-only">{action.label}</span>
  const shared = { className: 'play-card__action', 'aria-describedby': describedBy, onClick: action.onClick }
  if (action.to !== undefined) return <Link {...shared} to={action.to} replace={action.replace}>{content}</Link>
  if (action.href !== undefined) return <a {...shared} href={action.href}>{content}</a>
  return <button {...shared} type="button" aria-pressed={pressed}>{content}</button>
}

/** A neutral surface that groups one topic: a title, an optional description and actions, then content. */
export const Card = forwardRef<HTMLElement, CardProps>(function Card(
  {
    title, description, icon, actions, as: Element = 'div', headingLevel = 2, selected, selectionTone = 'neutral', action,
    className, children, ...props
  },
  ref,
) {
  const titleId = useId()
  const descriptionId = useId()
  const Heading = `h${headingLevel}` as const
  const hasHeader = Boolean(title || description || actions)
  const named = Boolean(title) && (Element === 'section' || Element === 'article')
  return (
    <Element
      // The union of intrinsic elements cannot narrow the ref type; every option is an HTMLElement.
      ref={ref as never}
      aria-labelledby={named && !props['aria-label'] ? titleId : undefined}
      data-selected={selected ? '' : undefined}
      className={cn(
        'play-card',
        action && 'play-card--action',
        selected && 'play-card--selected',
        selected && selectionTone === 'media' && 'play-card--selected-media',
        className,
      )}
      {...props}
    >
      {/* First, so Tab reaches the card's action before the controls inside it. */}
      {action && <CardActionControl action={action} pressed={selected} describedBy={description ? descriptionId : undefined} />}
      {hasHeader && (
        <div className="play-card__header">
          {(title || description) && (
            <div className="play-card__heading">
              {title && (
                <Heading id={titleId} className="play-card__title">
                  {icon && <span className="play-card__icon" aria-hidden="true">{icon}</span>}
                  {title}
                </Heading>
              )}
              {description && <p id={descriptionId} className="play-card__description">{description}</p>}
            </div>
          )}
          {actions && <div className="play-card__actions">{actions}</div>}
        </div>
      )}
      {children !== undefined && children !== null && children !== false && <div className="play-card__body">{children}</div>}
    </Element>
  )
})
